import { chatHistorySampleData } from '../constants/chatHistory'

import {
  Approval,
  ChatMessage,
  Configuration,
  Conversation,
  ConversationRequest,
  CosmosDBHealth,
  CosmosDBStatus,
  Library,
  Prompt,
  Template,
  UserInfo,
  Country,
  CountryList
} from './models'

import { getAccessToken } from '../auth/auth';

const API_URL = import.meta.env.VITE_API_URL

export async function conversationApi(options: ConversationRequest, abortSignal: AbortSignal): Promise<Response | null>{
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/conversation`, {
    method: 'POST',
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"},
    body: JSON.stringify({
      messages: options.messages
    }),
    signal: abortSignal
  })

  return response
}

export async function checkTemplateExists(templateName: string): Promise<any> {
  const token = await getAccessToken()
  try {
    const response = await fetch(`${API_URL}/template_exists`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        templateName: templateName
      })
    })

    if (!response.ok) throw new Error('Failed to check if template exists')

    const data = await response.json()

    return data.exists
  } catch (error) {
    throw error
  }
}

export async function generateDocx(report: string, templateName: string): Promise<any> {
  const token = await getAccessToken()
  try {
    const response = await fetch(`${API_URL}/download_docx`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        report: report,
        templateName: templateName
      })
    })

    if (response.ok) {
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${templateName}.docx` // Name of the downloaded file
      link.click()
    } else {
      throw new Error('Failed to generate DOCX from answer')
    }
  } catch (error) {
    throw error
  }
}

export async function checkCache(templateValue: string, fields: { [key: string]: string }, abortSignal: AbortSignal): Promise<any> {
  const token = await getAccessToken()
  try {
    const cacheResponse = await fetch(`${API_URL}/report/cache`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        templateName: templateValue,
        fields: fields // Make sure the fields match your API expectations
      }),
      signal: abortSignal
    })

    const data = await cacheResponse.json()

    if (cacheResponse.ok && data?.report) {
      return data.report
    } else {
      return null
    }
  } catch (error) {
    throw error
  }
}

export async function pollWizardResult(taskId: string, abortSignal: AbortSignal): Promise<any> {
  const token = await getAccessToken()
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        if (abortSignal.aborted) {
          clearInterval(interval)
          reject(new Error('Task was cancelled'))
          return
        }

        const response = await fetch(`${API_URL}/wizard/status/${taskId}`, { signal: abortSignal,
          headers: {
            Authorization: `Bearer ${token}`,
          }
         });

        if (!response.ok) throw new Error('Failed to fetch task status')

        const data = await response.json()

        if (abortSignal.aborted) {
          // Double-check
          clearInterval(interval)
          reject(new Error('Task was cancelled'))
          return
        }

        if (data.status === 'completed') {
          clearInterval(interval)
          resolve(data.result)
        } else if (data.status === 'cancelled') {
          clearInterval(interval)
          reject(new Error('Task was cancelled'))
        } else if (data.status === 'error') {
          clearInterval(interval)
          reject(new Error(data.result))
        }
      } catch (error) {
        clearInterval(interval)
        reject(error)
      }
    }, 2000)
  })
}

export async function wizardApi(options: ConversationRequest, abortSignal: AbortSignal): Promise<string> {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/wizard`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: options.messages }),
    signal: abortSignal
  })

  if (!response.ok) {
    throw new Error('Failed to start wizard request')
  }

  const data = await response.json()
  return data.task_id // Return the task_id
}

export async function getUserInfo(): Promise<UserInfo[]> {
  const response = await fetch(`${API_URL}/me`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`, // send JWT
    },
  })

  if (!response.ok) {
    console.log('No identity provider found. Access to chat will be blocked.')
    return []
  }

  const payload = await response.json()
  return payload
} 

// export const fetchChatHistoryInit = async (): Promise<Conversation[] | null> => {
export const fetchChatHistoryInit = (): Conversation[] | null => {
  // Make initial API call here

  return chatHistorySampleData
}

export const historyList = async (offset = 0): Promise<Conversation[] | null> => {
  const token = await getAccessToken()
  if (!token) {
    return null
  }
  const response = await fetch(`${API_URL}/history/list?offset=${offset}`, {
    method: 'GET',
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const conversations: Conversation[] = await Promise.all(
        payload.map(async (conv: any) => {
          let convMessages: ChatMessage[] = []
          convMessages = await historyRead(conv.id)
            .then(res => {
              return res
            })
            .catch(err => {
              console.error('error fetching messages: ', err)
              return []
            })
          const conversation: Conversation = {
            id: conv.id,
            title: conv.title,
            date: conv.createdAt,
            messages: convMessages
          }
          return conversation
        })
      )
      return conversations
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/history/read`, {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(async res => {
      if (!res) {
        return []
      }
      const payload = await res.json()
      const messages: ChatMessage[] = []
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            date: msg.createdAt,
            content: msg.content,
            feedback: msg.feedback ?? undefined
          }
          messages.push(message)
        })
      }
      return messages
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return []
    })
  return response
}

export const historyGenerate = async (
  options: ConversationRequest,
  abortSignal: AbortSignal,
  convId?: string
): Promise<Response | null> => {
  let body
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  if (convId) {
    body = JSON.stringify({
      conversation_id: convId,
      messages: options.messages
    })
  } else {
    body = JSON.stringify({
      messages: options.messages
    })
  }
  const response = await fetch(`${API_URL}/history/generate`, {
    method: 'POST',
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"},
    body: body,
    signal: abortSignal
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return new Response()
    })
  return response
}

export const historyUpdate = async (messages: ChatMessage[], convId: string): Promise<Response | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/history/update`, {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages
    }),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDelete = async (convId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/history/delete`, {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDeleteAll = async (): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/history/delete_all`, {
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyClear = async (convId: string): Promise<Response | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/history/clear`, {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyRename = async (convId: string, title: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/history/rename`, {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      title: title
    }),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/history/ensure`, {
    method: 'GET',
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(async res => {
      const respJson = await res.json()
      let formattedResponse
      if (respJson.message) {
        formattedResponse = CosmosDBStatus.Working
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking
        } else if (res.status === 401) {
          formattedResponse = CosmosDBStatus.InvalidCredentials
        } else if (res.status === 422) {
          formattedResponse = respJson.error
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured
        }
      }
      if (!res.ok) {
        return {
          cosmosDB: false,
          status: formattedResponse
        }
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse
        }
      }
    })
    .catch(err => {
      console.error('There was an issue fetching your data.')
      return {
        cosmosDB: false,
        status: err
      }
    })
  return response
}

export const frontendSettings = async (): Promise<Response | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/frontend_settings`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const indexesList = async (): Promise<Response | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/indexes/list`, {
    method: "GET",
    headers: { 
      Authorization: `Bearer ${token}`}
  })
    .then(res => {
      return res.json()
    })
    .catch(err => {
      console.error('There was an issue fetching the Azure AI Search indexes list.')
      return null
    })

  return response
}

export const templatesList = async (): Promise<Response | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/get_templates`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    }
  }).then((res) => {
    return res.json()
  }).catch((err) => {
    console.error("There was an issue fetching the Templates for Wizard.");
    return null
  })

  return response
}

export const environmentVariablesList = async (): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/environment_variables`, {
    method: "GET",
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    }
  }).then((res) => {
    return res.json()
  }).catch((_err) => {
    console.error("There was an issue fetching the environment variables from your server.");
    return null
  })

  return response
}

export const approvalList = async (): Promise<Approval[] | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/approval/list`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const libraries: Approval[] = await Promise.all(
        payload.map(async (conv: any) => {
          const library: Approval = {
            id: conv.id,
            library: conv.library,
            site: conv.site,
            fileName: conv.fileName,
            status: conv.status,
            timestamp: conv.timestamp,
            sizeMb: conv.sizeMb
          }
          return library
        })
      )
      return libraries
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const approvalUpdate = async (approval: string, approvalId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/approval/update`, {
    method: 'POST',
    body: JSON.stringify({
      approval_id: approvalId,
      approval: approval
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const configurationList = async (): Promise<Configuration[] | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/configuration/list`, {
    method: 'GET',
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const configurations: Configuration[] = await Promise.all(
        payload.map(async (conv: any) => {
          const configuration: Configuration = {
            id: conv.id,
            name: conv.name,
            description: conv.description,
            value: conv.value
          }
          return configuration
        })
      )
      return configurations
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const configurationUpdate = async (configuration: Configuration, confId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/configuration/update`, {
    method: 'POST',
    body: JSON.stringify({
      configuration_id: confId,
      configuration: configuration
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

/* export const configurationList = async (): Promise<Response | null> => {
    const response = await fetch("/configuration/list", {
        method: "GET",
    }).then((res) => {
        return res.json()
    }).catch((_err) => {
        console.error("There was an issue fetching the Configurations list.");
        return null
    })

  return response
} */

export const libraryList = async (): Promise<Library[] | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/library/list`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const libraries: Library[] = await Promise.all(
        payload.map(async (conv: any) => {
          const library: Library = {
            id: conv.id,
            name: conv.name,
            url: conv.url
          }
          return library
        })
      )
      return libraries
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const libraryGenerate = async (
  library: Library
): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/library/create`, {
    method: 'POST',
    body: JSON.stringify({
      library: library
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const libraryUpdate = async (library: Library, libId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/library/update`, {
    method: 'POST',
    body: JSON.stringify({
      library_id: libId,
      library: library
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const libraryDelete = async (libId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/library/delete`, {
    method: 'POST',
    body: JSON.stringify({
      library_id: libId
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue deleting the library.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

/* export const libraryList = async (): Promise<Response | null> => {
    const response = await fetch("/library/list", {
        method: "GET",
    }).then((res) => {
        return res.json()
    }).catch((_err) => {
        console.error("There was an issue fetching the SharePoint libraries list.");
        return null
    })

  return response
} */

/* export const templateList = async (): Promise<Response | null> => {
  const response = await fetch("/template/list", {
      method: "GET",
  }).then((res) => {
      return res.json()
  }).catch((_err) => {
      console.error("There was an issue fetching the Document Wizard list.");
      return null
  })

return response
} */

export const templateList = async (): Promise<Template[] | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/template/list`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(async res => {
      const payload = await res.json()
      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const libraries: Template[] = await Promise.all(
        payload.map(async (conv: any) => {
          const library: Template = {
            id: conv.id,
            name: conv.name,
            source: conv.source,
            webConfig: conv.webConfig,
            status: conv.status,
            prompts: conv.prompts,
            fields: conv.fields,
            docxFile: conv.docxFile
          }
          return library
        })
      )
      return libraries
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const templateTest = async (
  template: Template,
  fieldValues: { [key: string]: string }
): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/template/test`, {
    method: 'POST',
    body: JSON.stringify({
      template: template,
      fieldValues: fieldValues
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const templateGenerate = async (
  template: Template
): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/template/create`, {
    method: 'POST',
    body: JSON.stringify({
      template: {
        ...template,
        fields: template.fields || [], // Ensure 'fields' is included as an empty array if missing
        status: template.status || 'Inactive'
      }
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const templateUpdate = async (template: Template, templateId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/template/update`, {
    method: 'POST',
    body: JSON.stringify({
      template_id: templateId,
      template: template
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const templateDelete = async (templateId: string): Promise<Response> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/template/delete`, {
    method: 'POST',
    body: JSON.stringify({
      template_id: templateId
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue deleting the template.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyMessageFeedback = async (messageId: string, feedback: string): Promise<Response | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/history/message_feedback`, {
    method: 'POST',
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback
    }),
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue logging feedback.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const countryListS = async (): Promise<Country[] | null> => {
  const token = await getAccessToken()
  if (!token) {
    return null
  }
  const response = await fetch(`${API_URL}/country/list`, {
    method: 'GET',
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  })
    .then(async res => {
      const payload = await res.json()

      if (!Array.isArray(payload)) {
        console.error('There was an issue fetching your data.')
        return null
      }
      const libraries: Country[] = await Promise.all(
        payload.map(async (conv: any) => {
          const library: Country = {
            country: conv.country
          }
          console.log(library)
          return library
        })
      )
      return libraries
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}

export const countryList = async (): Promise<Response | null> => {
  const token = await getAccessToken()
  if (!token) {
    return null
  }
  const response = await fetch(`${API_URL}/country/list`, {
    method: "GET",
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  }).then((res) => {
    return res.json()
  }).catch((err) => {
    console.error("There was an issue fetching the countries list.");
    return null
  })
  return response
}


export const fetchLitReviewKeywords = async (
  query: string,
  previousKeywords?: string
): Promise<string[] | null> => {
  const token = await getAccessToken()
  const response = await fetch(`${API_URL}/litReview/keywords`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      keywords: previousKeywords
    })
  })
    .then(async res => {
      const payload = await res.json()

      // ✅ Correct: check and return the "keywords" field
      if (!payload.keywords || !Array.isArray(payload.keywords)) {
        console.error('Expected an array of keywords.')
        return null
      }

      return payload.keywords as string[]
    })
    .catch(_err => {
      console.error('There was an issue fetching the keywords.')
      return null
    })

  return response
}

export const fetchQueryToKeywords = async (
  query: string
): Promise<string[] | null> => {
  const token = await getAccessToken()
  try {
    const res = await fetch(`${API_URL}/litReview/querykeywords`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query })
    })

    const payload = await res.json()

    if (!payload.keywords || !Array.isArray(payload.keywords)) {
      console.error('Invalid keywords response')
      return null
    }

    return payload.keywords
  } catch (err) {
    console.error('There was an issue fetching keywords from query.', err)
    return null
  }
}

//GET Articles
export interface Article {
  id: string
  title: string
  authors: string[]
  abstract?: string
  year?: number
  source?: string
  [key: string]: any // fallback for extra fields
}

export interface GetArticlesParams {
  keywords?: string
  start_date?: string
  end_date?: string
  language?: string
  document_type?: string
  sources?: string[]
}

export async function fetchArticles(params: GetArticlesParams): Promise<Article[]> {
  const token = await getAccessToken()
  try {
    const response = await fetch(`${API_URL}/litReview/articles`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params)
    })

    if (!response.ok) {
      throw new Error(`Error fetching articles: ${response.statusText}`)
    }

    const data: Article[] = await response.json()
    return data
  } catch (error) {
    console.error('fetchArticles failed', error)
    throw error
  }
}

export interface GetLitReviewParams {
  papers: Article[]
  query: string
}
export type LitReviewOutput = {
  conclusion: string
  context: string
  intro: string
  summaries: string[]
  bib: string[]
}

export async function fetchLitReview(params: GetLitReviewParams): Promise<LitReviewOutput> {
  const token = await getAccessToken()
  try {
    const response = await fetch(`${API_URL}/litReview/litReview`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params)
    })

    if (!response.ok) {
      throw new Error(`Error fetching lit review: ${response.statusText}`)
    }

    // Flask returns `jsonify(litReview)` which is likely a string, so parse accordingly
    const data = await response.json()

    // if your backend returns { litReview: "..." }, you'd access data.litReview instead
    return data
  } catch (error) {
    console.error('fetchLitReview failed', error)
    throw error
  }
}

export async function downloadLitReviewDocx(litReviewData: any): Promise<void> {
  const token = await getAccessToken()
  try {
    const response = await fetch(`${API_URL}/litReview/exportDocx`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(litReviewData)
    })

    if (!response.ok) {
      throw new Error(`Error downloading DOCX: ${response.statusText}`)
    }

    // Get the binary file as a blob
    const blob = await response.blob()

    // Create a temporary download link
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'literature_review.docx'
    document.body.appendChild(a)
    a.click()

    // Clean up
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('downloadLitReviewDocx failed', error)
    throw error
  }
}

export async function downloadLitReviewCsv(litReviewData: any): Promise<void> {
  const token = await getAccessToken()
  try {
    const response = await fetch(`${API_URL}/litReview/exportCsv`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(litReviewData)
    })

    if (!response.ok) {
      throw new Error(`Error downloading CSV: ${response.statusText}`)
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = 'literature_review_bibliography.csv'
    document.body.appendChild(a)
    a.click()

    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('downloadLitReviewCsv failed', error)
    throw error
  }
}

export const filterYears = async (): Promise<string | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/year/list`, {
    method: "GET",
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  }).then((res) => {
    return res.json()
  }).catch((err) => {
    console.error("There was an issue fetching the year list.");
    return null
  })
  console.log(response)
  return response
}

export const yearPrompt = async (): Promise<string | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/year/prompt`, {
    method: "GET",
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  }).then((res) => {
    return res.json()
  }).catch((err) => {
    console.error("There was an issue fetching the year prompt.");
    return null
  })
  console.log(response)
  return response
}

export const countriesPrompt = async (): Promise<string | null> => {
  const token = await getAccessToken()
  if (!token)
  {
    return null
  }
  const response = await fetch(`${API_URL}/country/prompt`, {
    method: "GET",
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"}
  }).then((res) => {
    return res.json()
  }).catch((err) => {
    console.error("There was an issue fetching the country prompt.");
    return null
  })
  return response
}

export const getWhitelists = async (): Promise<string[]> => {
  const token = await getAccessToken()
  try {
    const res = await fetch(`${API_URL}/whitelist/list`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });

    if (!res.ok) {
      console.error('Unable to fetch whitelists')
      return []
    }

    const data = await res.json()

    // Validate result is an array of strings
    if (!Array.isArray(data)) {
      console.error('Invalid whitelist response format', data)
      return []
    }

    return data
  } catch (err) {
    console.error('There was an issue fetching the whitelists.', err)
    return []
  }
}
